import React, { useState, useEffect } from 'react';
import './App.css';

// ============================================================================
// SEO HEAD
// ============================================================================
const SEOHead = ({ title, description, image, url, type = 'website' }) => {
  useEffect(() => {
    document.title = title;
    const setMeta = (name, content) => {
      let tag = document.querySelector(`meta[${name.startsWith('og:') || name.startsWith('twitter:') ? 'property' : 'name'}="${name}"]`);
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute(name.startsWith('og:') || name.startsWith('twitter:') ? 'property' : 'name', name);
        document.head.appendChild(tag);
      }
      tag.content = content;
    };
    setMeta('description', description);
    setMeta('og:title', title);
    setMeta('og:description', description);
    setMeta('og:image', image);
    setMeta('og:url', url);
    setMeta('og:type', type);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    setMeta('twitter:image', image);
  }, [title, description, image, url, type]);
  return null;
};

// ============================================================================
// NAVIGATION
// ============================================================================
const Navigation = ({ currentPage, setCurrentPage }) => {
  const [menuOpen, setMenuOpen] = useState(false);

  const navigate = (page) => {
    setCurrentPage(page);
    setMenuOpen(false);
  };

  const links = [
    { id: 'home', label: 'Home' },
    { id: 'gallery', label: 'Gallery' },
    { id: 'services', label: 'Services' },
    { id: 'book', label: 'Book' },
    { id: 'contact', label: 'Contact' },
  ];

  return (
    <nav className="nav-bar">
      <button className="nav-logo-btn" onClick={() => navigate('home')} aria-label="Home">
        <span className="nav-brand-text">Hole Hearted Piercing</span>
      </button>

      {/* Desktop links */}
      <div className="nav-links-desktop">
        {links.map(l => (
          <button
            key={l.id}
            className={`nav-link ${currentPage === l.id ? 'active' : ''}`}
            onClick={() => navigate(l.id)}
          >
            {l.label}
          </button>
        ))}
      </div>

      {/* Hamburger button (mobile) */}
      <button
        className={`hamburger ${menuOpen ? 'open' : ''}`}
        onClick={() => setMenuOpen(!menuOpen)}
        aria-label="Toggle menu"
      >
        <span /><span /><span />
      </button>

      {/* Mobile dropdown */}
      <div className={`nav-mobile-menu ${menuOpen ? 'open' : ''}`}>
        {links.map(l => (
          <button
            key={l.id}
            className={`nav-link-mobile ${currentPage === l.id ? 'active' : ''}`}
            onClick={() => navigate(l.id)}
          >
            {l.label}
          </button>
        ))}
      </div>
    </nav>
  );
};

// ============================================================================
// HOME PAGE
// ============================================================================
const HomePage = ({ setCurrentPage }) => (
  <>
    <SEOHead
      title="Hole Hearted Piercing | Professional Body Piercing in Orpington"
      description="Expert body piercing by Lacie at Hole Hearted Piercing. Located at Serenity Ink, Orpington, Kent. Appointments & walk-ins welcome."
      image="https://holehearted.online/og-image.png"
      url="https://holehearted.online"
    />

    {/* HERO */}
    <section className="hero">
      <div className="hero-rose" aria-hidden="true" />
      <div className="hero-content">
        <picture>
          <source srcSet="/hero_logo.webp" type="image/webp" />
          <img src="/hero_logo.png" alt="Hole Hearted Piercing" className="hero-logo" />
        </picture>
        <p className="hero-tagline">Professional body piercing at Serenity Ink, Orpington</p>
        <div className="hero-buttons">
          <button className="btn-primary" onClick={() => setCurrentPage('book')}>Book an Appointment</button>
          <button className="btn-secondary" onClick={() => setCurrentPage('gallery')}>View Gallery</button>
        </div>
      </div>
    </section>

    {/* ABOUT */}
    <section className="about">
      <div className="about-content">
        <h2>About Lacie</h2>
        <div className="divider">☩ ──── ✠ ──── ☩</div>
        <p>
          Lacie isn't just a piercer — she's an artist who happens to use needles.
          Based permanently at <strong>Serenity Ink</strong> in Orpington, she brings
          precision, care, and a whole lot of personality to every single appointment.
        </p>
        <p>
          Whether it's your very first piercing or you're adding to a curated ear,
          you'll leave feeling looked after, informed, and genuinely excited.
          No judgement, no rush — just clean technique and good vibes.
        </p>
        <p className="about-quote">"Piercing is personal. I treat it that way."</p>
        <button className="btn-secondary" onClick={() => setCurrentPage('book')}>Book with Lacie</button>
      </div>
    </section>

    {/* FEATURE CARDS */}
    <section className="features">
      <div className="feature-card">
        <div className="feature-icon">☠</div>
        <h3>Precise</h3>
        <p>Every placement is measured, planned, and executed with care — no guesswork, no wonky angles.</p>
      </div>
      <div className="feature-card">
        <div className="feature-icon">✠</div>
        <h3>Safe</h3>
        <p>Sterile single-use equipment, implant-grade jewellery, and thorough aftercare guidance every time.</p>
      </div>
      <div className="feature-card">
        <div className="feature-icon">☩</div>
        <h3>Welcoming</h3>
        <p>Nervous? First timer? Covered in piercings already? Everyone gets the same warm welcome.</p>
      </div>
    </section>
  </>
);

// ============================================================================
// GALLERY PAGE
// ============================================================================
//
// LIVE INSTAGRAM FEED — FREE via Behold.so
// To activate the live feed:
//   1. Go to https://behold.so and create a free account
//   2. Connect Instagram account @hole_hearted_666
//   3. Create a new feed, choose your layout, copy the Feed ID
//   4. Paste it below replacing the empty string
//
const BEHOLD_FEED_ID = ''; // ← e.g. 'AbCdEfGh12345'

const GalleryPage = () => {
  const [slide, setSlide] = useState(0);
  const total = 6;

  // Load Behold script when feed ID is present
  useEffect(() => {
    if (!BEHOLD_FEED_ID) return;
    if (document.querySelector('script[src="https://w.behold.so/widget.js"]')) return;
    const s = document.createElement('script');
    s.src = 'https://w.behold.so/widget.js';
    s.type = 'module';
    document.head.appendChild(s);
  }, []);

  // Auto-advance carousel when no live feed
  useEffect(() => {
    if (BEHOLD_FEED_ID) return;
    const t = setInterval(() => setSlide(p => (p + 1) % total), 3200);
    return () => clearInterval(t);
  }, []);

  return (
    <>
      <SEOHead
        title="Gallery | Hole Hearted Piercing"
        description="View Lacie's professional piercing portfolio. Follow @hole_hearted_666 on Instagram for the latest work."
        image="https://holehearted.online/og-image.png"
        url="https://holehearted.online/gallery"
      />
      <section className="gallery-section">
        <h2>Gallery</h2>
        <div className="divider">☩ ──── ✠ ──── ☩</div>

        {BEHOLD_FEED_ID ? (
          /* ── LIVE FEED (Behold.so) ── */
          <div className="behold-wrapper">
            <div data-behold-id={BEHOLD_FEED_ID}></div>
          </div>
        ) : (
          /* ── CAROUSEL PLACEHOLDER ── */
          <>
            <p className="gallery-intro">
              Live portfolio coming soon — follow{' '}
              <a href="https://instagram.com/hole_hearted_666" target="_blank" rel="noopener noreferrer">
                @hole_hearted_666
              </a>{' '}
              on Instagram for the full portfolio and latest work.
            </p>

            <div className="carousel-wrap">
              <div
                className="carousel-track"
                style={{ transform: `translateX(-${slide * (100 / 3)}%)` }}
              >
                {Array.from({ length: total }).map((_, i) => (
                  <a
                    key={i}
                    href="https://instagram.com/hole_hearted_666"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`carousel-tile ${i === slide ? 'active' : ''}`}
                  >
                    <div className="carousel-tile-inner">
                      <span className="carousel-icon">✠</span>
                      <span className="carousel-handle">@hole_hearted_666</span>
                      <span className="carousel-cta">View on Instagram</span>
                    </div>
                  </a>
                ))}
              </div>

              {/* Dot indicators */}
              <div className="carousel-dots">
                {Array.from({ length: total }).map((_, i) => (
                  <button
                    key={i}
                    className={`carousel-dot ${i === slide ? 'active' : ''}`}
                    onClick={() => setSlide(i)}
                    aria-label={`Slide ${i + 1}`}
                  />
                ))}
              </div>
            </div>
          </>
        )}

        <div className="insta-follow-banner">
          <p>Follow <strong>@hole_hearted_666</strong> for the latest piercings, healed results & inspiration</p>
          <a
            href="https://instagram.com/hole_hearted_666"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary"
          >
            Follow on Instagram
          </a>
        </div>
      </section>
    </>
  );
};

// ============================================================================
// SERVICES PAGE
// ============================================================================
const ServicesPage = ({ setCurrentPage }) => {
  const months = [
    {
      name: 'June 2026', startDay: 1, days: 30,
      avail: {
        1:0,2:0,3:0,4:1,5:1,6:1,
        7:-1,8:0,9:0,10:0,11:0,12:1,13:1,
        14:-1,15:0,16:1,17:0,18:1,19:1,20:1,
        21:-1,22:0,23:1,24:0,25:1,26:1,27:1,
        28:-1,29:0,30:1,
      },
    },
    {
      name: 'July 2026', startDay: 3, days: 31,
      avail: {
        1:0,2:1,3:1,4:1,
        5:-1,6:0,7:1,8:0,9:1,10:1,11:1,
        12:-1,13:0,14:1,15:0,16:1,17:1,18:1,
        19:-1,20:0,21:1,22:0,23:1,24:1,25:1,
        26:-1,27:0,28:1,29:0,30:1,31:1,
      },
    },
    {
      name: 'August 2026', startDay: 6, days: 31,
      avail: {
        1:1,
        2:-1,3:0,4:1,5:0,6:1,7:1,8:1,
        9:-1,10:0,11:1,12:0,13:0,14:1,15:0,
        16:-1,17:0,18:0,19:0,20:1,21:1,22:1,
        23:-1,24:0,25:1,26:0,27:1,28:1,29:1,
        30:-1,31:0,
      },
    },
  ];

  const dayNames = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];

  return (
    <>
      <SEOHead
        title="Services & Pricing | Hole Hearted Piercing"
        description="Professional ear, face and body piercing services in Orpington. Real prices for ear, face and body piercings by Lacie at Serenity Ink."
        image="https://holehearted.online/og-image.png"
        url="https://holehearted.online/services"
      />

      {/* ── PRICING ── */}
      <section className="services-section">
        <h2>Services & Pricing</h2>
        <div className="divider">✦ ── ☠ ── ✦</div>

        {[
          {
            title: 'Ears',
            items: [
              ['Lobe',           '£15'],
              ['Lobes (pair)',   '£30'],
              ['Helix',         '£30'],
              ['Flat',          '£30'],
              ['Daith',         '£30'],
              ['Conch',         '£30'],
              ['Rook',          '£30'],
              ['Tragus',        '£30'],
              ['Anti Tragus',   '£40'],
              ['Surface Tragus','£40'],
              ['Industrial',    '£50'],
            ],
          },
          {
            title: 'Face',
            items: [
              ['Smiley',  '£25'],
              ['Nostril', '£30'],
              ['Labret',  '£30 · 2 for £50'],
              ['Eyebrow', '£35'],
              ['Septum',  '£40'],
              ['Medusa',  '£40'],
              ['Dahlia',  '£40 · 2 for £70'],
              ['Monroe',  '£50'],
              ['Bridge',  '£50'],
            ],
          },
          {
            title: 'Body',
            items: [
              ['Navel',   '£45'],
              ['Surface', '£40'],
              ['Nipple',  '£50 · 2 for £90'],
            ],
          },
        ].map(cat => (
          <div className="pricing-category" key={cat.title}>
            <h3>{cat.title}</h3>
            <div className="pricing-list">
              {cat.items.map(([name, price]) => (
                <div className="pricing-item" key={name}>
                  <span>{name}</span>
                  <span className="price-val">{price}</span>
                </div>
              ))}
            </div>
          </div>
        ))}

        <div className="pricing-note">
          <p>Any questions or enquiries for piercings not on the list? Drop a DM to <a href="https://instagram.com/hole_hearted_666" target="_blank" rel="noreferrer">@hole_hearted_666</a></p>
        </div>

        <div className="services-cta">
          <button className="btn-primary" onClick={() => setCurrentPage('book')}>Book Now</button>
        </div>
      </section>

      {/* ── AVAILABILITY ── */}
      <section className="availability-section">
        <h2>Availability</h2>
        <div className="divider">✦ ── ☠ ── ✦</div>
        <p className="avail-intro">Walk-ins welcome on pink days. DM <a href="https://instagram.com/hole_hearted_666" target="_blank" rel="noreferrer">@hole_hearted_666</a> to book in advance.</p>

        <div className="avail-legend">
          <span className="leg-walkin">💕 Walk-ins welcome</span>
          <span className="leg-notin">🖤 Not in</span>
          <span className="leg-closed">🚫 Shop closed</span>
        </div>

        <div className="avail-calendars">
          {months.map(month => (
            <div className="avail-month" key={month.name}>
              <h3 className="avail-month-title">{month.name}</h3>
              <div className="avail-grid">
                {dayNames.map(d => <div className="avail-dayname" key={d}>{d}</div>)}
                {Array.from({length: month.startDay}).map((_,i) => (
                  <div key={`e${i}`} className="avail-empty" />
                ))}
                {Array.from({length: month.days}).map((_,i) => {
                  const day = i + 1;
                  const s = month.avail[day];
                  const cls = s === 1 ? 'avail-walkin' : s === -1 ? 'avail-closed' : 'avail-notin';
                  const icon = s === 1 ? '💕' : s === -1 ? '🚫' : '🖤';
                  return (
                    <div className={`avail-day ${cls}`} key={day}>
                      <span className="avail-num">{day}</span>
                      <span className="avail-icon">{icon}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
};

// ============================================================================
// BOOK PAGE
// ============================================================================
const BookPage = () => {
  const [bookingType, setBookingType] = useState('appointment');

  return (
    <>
      <SEOHead
        title="Book an Appointment | Hole Hearted Piercing"
        description="Book a professional piercing appointment with Lacie at Hole Hearted Piercing, Serenity Ink, Orpington."
        image="https://holehearted.online/og-image.png"
        url="https://holehearted.online/book"
      />
      <section className="book-section">
        <h2>Book an Appointment</h2>
        <div className="divider">☩ ──── ✠ ──── ☩</div>

        <div className="booking-toggle">
          <button className={`toggle-btn ${bookingType === 'appointment' ? 'active' : ''}`} onClick={() => setBookingType('appointment')}>
            Schedule an Appointment
          </button>
          <button className={`toggle-btn ${bookingType === 'walkin' ? 'active' : ''}`} onClick={() => setBookingType('walkin')}>
            Walk-In Info
          </button>
        </div>

        {bookingType === 'appointment' && (
          <div className="booking-form-container">
            <div className="instagram-book">
              <p className="instagram-book-intro">
                Ready to get pierced? Slide into Lacie's DMs on Instagram to book your appointment — she'll get back to you fast.
              </p>
              <a
                href="https://ig.me/m/hole_hearted_666"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary btn-instagram"
              >
                📩 &nbsp;Message Lacie on Instagram
              </a>
              <p className="instagram-book-handle">
                <a href="https://instagram.com/hole_hearted_666" target="_blank" rel="noopener noreferrer">
                  @hole_hearted_666
                </a>
              </p>
              <ul className="instagram-book-tips">
                <li>Tell Lacie which piercing you want</li>
                <li>Include any reference photos you love</li>
                <li>Ask about availability — she'll sort a date that suits you</li>
              </ul>
            </div>
          </div>
        )}

        {bookingType === 'walkin' && (
          <div className="walkin-info">
            <h3>Walk-In Piercing</h3>
            <p>Walk-ins are welcome at <strong>Serenity Ink, Orpington</strong> subject to Lacie's availability.</p>
            <p><strong>Best approach:</strong> Check the <a href="https://holehearted.online/services">availability calendar</a> on the Services page, or message on Instagram before heading in.</p>
            <a
              href="https://ig.me/m/hole_hearted_666"
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary btn-instagram"
            >
              📩 &nbsp;Check Availability on Instagram
            </a>
          </div>
        )}
      </section>
    </>
  );
};

// ============================================================================
// CONTACT PAGE
// ============================================================================
const ContactPage = () => {
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const data = new FormData(form);
    try {
      await fetch('/', { method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' }, body: new URLSearchParams(data).toString() });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    }
  };

  return (
    <>
      <SEOHead
        title="Contact | Hole Hearted Piercing"
        description="Get in touch with Hole Hearted Piercing. Located at Serenity Ink in Orpington, Kent."
        image="https://holehearted.online/og-image.png"
        url="https://holehearted.online/contact"
      />
      <section className="contact-section">
        <h2>Find Us</h2>
        <div className="divider">☩ ──── ✠ ──── ☩</div>

        <div className="contact-container">
          <div className="contact-info">
            <h3>Get in Touch</h3>
            <p><strong>Location</strong><br />Serenity Ink<br />Orpington, Kent</p>
            <p>
              <strong>Instagram</strong><br />
              <a href="https://instagram.com/hole_hearted_666" target="_blank" rel="noopener noreferrer">@hole_hearted_666</a>
            </p>
            <p><strong>Appointments & Enquiries</strong><br />Use the form or message on Instagram</p>

            <div className="map-container">
              <iframe
                title="Serenity Ink Orpington"
                src="https://maps.google.com/maps?q=Serenity+Ink,+Orpington,+Kent,+UK&output=embed"
                width="100%"
                height="250"
                style={{ border: 0, borderRadius: '4px' }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {submitted ? (
            <div className="form-success">
              <p>✦ Message received — we'll get back to you soon. ✦</p>
            </div>
          ) : (
            <form className="contact-form" name="contact" onSubmit={handleSubmit} data-netlify="true">
              <input type="hidden" name="form-name" value="contact" />
              <input type="text" name="name" placeholder="Your Name" required />
              <input type="email" name="email" placeholder="Your Email" required />
              <textarea name="message" placeholder="Your Message" rows="5" required />
              <button type="submit" className="btn-primary">Send Message</button>
            </form>
          )}
        </div>
      </section>
    </>
  );
};

// ============================================================================
// FOOTER
// ============================================================================
const Footer = ({ setCurrentPage }) => (
  <footer className="footer">
    <div className="footer-content">
      <div className="footer-nav">
        {['home','gallery','services','book','contact'].map(p => (
          <button key={p} className="footer-link" onClick={() => setCurrentPage(p)}>
            {p.charAt(0).toUpperCase() + p.slice(1)}
          </button>
        ))}
      </div>
      <p>Located at <strong>Serenity Ink</strong>, Orpington, Kent &nbsp;|&nbsp;
        <a href="https://instagram.com/hole_hearted_666" target="_blank" rel="noopener noreferrer">@hole_hearted_666</a>
      </p>
      <p>&copy; {new Date().getFullYear()} Hole Hearted Piercing. All rights reserved.</p>
      <p className="footer-credit">
        Built by <a href="https://marvanova.com" target="_blank" rel="noopener noreferrer">Marvanova</a>
      </p>
    </div>
  </footer>
);

// ============================================================================
// APP
// ============================================================================
export default function App() {
  const [currentPage, setCurrentPage] = useState('home');

  const renderPage = () => {
    switch (currentPage) {
      case 'home':     return <HomePage setCurrentPage={setCurrentPage} />;
      case 'gallery':  return <GalleryPage />;
      case 'services': return <ServicesPage setCurrentPage={setCurrentPage} />;
      case 'book':     return <BookPage />;
      case 'contact':  return <ContactPage />;
      default:         return <HomePage setCurrentPage={setCurrentPage} />;
    }
  };

  return (
    <div className="App">
      <Navigation currentPage={currentPage} setCurrentPage={setCurrentPage} />
      <main className="main-content">{renderPage()}</main>
      <Footer setCurrentPage={setCurrentPage} />
    </div>
  );
}
